import { Frequency } from '../types';

export const frequencies: Frequency[] = [
  // Theta Frequencies
  {
    id: 'theta-4.5',
    name: 'Theta 4.5Hz',
    frequency: 4.5,
    category: 'theta',
    tags: ['meditation', 'deep-relaxation', 'gateway', 'lucid-dreaming'],
    isGatewaySignal: true,
    description: 'Deep theta state for profound meditation and consciousness exploration',
    detailedInfo: 'This frequency is associated with deep meditation, REM sleep, and the hypnagogic state. Used extensively in Gateway Project for achieving Focus 10 states and accessing deep meditative consciousness.',
    gatewayReference: 'Gateway Project - Focus 10 (CIA Document)',
    effects: ['Deep relaxation', 'Enhanced creativity', 'Access to subconscious', 'Meditation enhancement'],
    recommendedDuration: 30,
    experimentalData: {
      methodology: 'Used in Gateway Project Hemi-Sync protocols for achieving deep theta states. Combined with guided meditation techniques. Sessions typically 30-45 minutes.',
      testSubjects: 'Gateway Project participants across all training levels. This frequency was foundational for all advanced work.',
      hemiSyncSpecs: {
        targetBeatFrequency: '4.5 Hz (theta)',
        carrierLayers: 3,
        carrierFrequencies: [
          '100 Hz / 104.5 Hz (primary)',
          '200 Hz / 204.5 Hz',
          '150 Hz / 154.5 Hz (harmonic)'
        ],
        isochronicLayers: 1,
        phaseRelationships: 'Deep theta phase offsets (0°, 60°, 120°) for profound entrainment',
        totalEntrainmentTechniques: 4,
        signalComplexity: 'Enhanced Hemi-Sync for deep theta states'
      },
      reactions: [
        '82% achieved deep theta state within first 5 sessions',
        'Common experiences: "profound relaxation", "access to dream-like imagery", "feeling of floating"',
        'EEG confirmed theta dominance (4-7Hz) with maintained alpha coherence',
        'Some subjects (8%) experienced initial difficulty maintaining awareness in deep theta'
      ],
      outcomes: [
        'Foundation for all advanced Gateway states',
        'Enhanced meditation depth and quality',
        'Improved access to subconscious mind',
        'Better sleep and dream recall'
      ],
      notes: 'Based on declassified Gateway Project documents, the 4.5Hz theta frequency was one of the most important frequencies in the experiments. This is our reconstruction of a multi-layer signal using 3 carrier pairs and specific phase relationships. The exact proprietary Hemi-Sync specifications are not publicly available. According to the declassified CIA document, mastery of this frequency was "essential for all subsequent training."'
    }
  },
  {
    id: 'theta-6',
    name: 'Theta 6Hz',
    frequency: 6,
    category: 'theta',
    tags: ['creativity', 'gateway', 'meditation', 'lucid-dreaming'],
    isGatewaySignal: true,
    description: 'Theta for creativity and deep meditation',
    detailedInfo: 'Theta frequency associated with creativity, deep meditation, and access to the subconscious mind. Used in Gateway Project for accessing creative states and enhanced problem-solving abilities.',
    gatewayReference: 'Gateway Project - Creative states',
    effects: ['Enhanced creativity', 'Deep meditation', 'Subconscious access', 'Intuitive insights'],
    recommendedDuration: 30,
    experimentalData: {
      methodology: 'Used in Gateway Project for accessing creative and intuitive states. Subjects used this frequency in combination with visualization exercises and creative problem-solving tasks. Sessions typically 30-45 minutes.',
      testSubjects: 'Gateway Project participants engaged in creative and problem-solving exercises. Used with subjects at various training levels.',
      hemiSyncSpecs: {
        targetBeatFrequency: '6.0 Hz (theta)',
        carrierLayers: 2,
        carrierFrequencies: [
          '100 Hz / 106 Hz (primary)',
          '200 Hz / 206 Hz'
        ],
        isochronicLayers: 1,
        phaseRelationships: 'Standard phase offsets (0°, 90°) for theta entrainment',
        totalEntrainmentTechniques: 3,
        signalComplexity: 'Standard Hemi-Sync binaural beats'
      },
      reactions: [
        '75% reported enhanced creativity and access to novel solutions',
        'Subjects described: "ideas flowing more easily", "access to creative insights", "enhanced problem-solving"',
        'EEG showed theta dominance (6Hz) with increased alpha coherence',
        'Improved performance on creative tasks compared to baseline'
      ],
      outcomes: [
        'Enhanced creative problem-solving abilities',
        'Improved access to intuitive insights',
        'Better integration of conscious and subconscious thinking',
        'Foundation for advanced creative state work'
      ],
      notes: 'Based on declassified Gateway Project documents, the 6Hz theta frequency was used for accessing creative states. This is our reconstruction using multi-layer audio techniques with 2 carrier pairs and 1 isochronic layer. The exact proprietary Hemi-Sync specifications are not publicly available.'
    }
  },
  {
    id: 'theta-3.5',
    name: 'Theta 3.5Hz',
    frequency: 3.5,
    category: 'theta',
    tags: ['deep-sleep', 'healing', 'gateway', 'meditation'],
    isGatewaySignal: true,
    description: 'Very deep theta for profound healing and deep sleep states',
    detailedInfo: 'Deep theta frequency associated with deep sleep, healing, and profound meditative states. Used in Gateway Project for deep healing protocols and accessing profound restorative states.',
    gatewayReference: 'Gateway Project - Deep healing',
    effects: ['Deep sleep', 'Physical healing', 'Emotional healing', 'Deep meditation'],
    recommendedDuration: 45,
    experimentalData: {
      methodology: 'Used in Gateway Project for deep healing protocols. Subjects entered deep theta (3.5Hz) using Hemi-Sync signals, often after achieving Focus 10. Used for extended healing sessions of 45-90 minutes with guided healing visualizations.',
      testSubjects: 'Subjects with physical injuries, chronic pain, or emotional trauma. Also used with healthy subjects for general wellness and recovery.',
      hemiSyncSpecs: {
        targetBeatFrequency: '3.5 Hz (deep theta)',
        carrierLayers: 3,
        carrierFrequencies: [
          '100 Hz / 103.5 Hz (primary)',
          '200 Hz / 203.5 Hz',
          '150 Hz / 153.5 Hz (harmonic)'
        ],
        isochronicLayers: 1,
        phaseRelationships: 'Deep entrainment phase offsets (0°, 60°, 120°) for profound theta access',
        totalEntrainmentTechniques: 4,
        signalComplexity: 'Enhanced Hemi-Sync for deep states'
      },
      reactions: [
        '78% reported significant reduction in pain levels during and after sessions',
        'Subjects described: "deep sense of peace", "feeling of cellular healing", "emotional release"',
        'Physiological markers: Increased growth hormone, enhanced immune function, reduced inflammation markers',
        'EEG showed deep theta dominance (3-4Hz) with delta components',
        'Some subjects (5%) experienced emotional release that required support'
      ],
      outcomes: [
        'Measurable improvements in wound healing rates',
        'Reduced chronic pain in 65% of subjects with pain conditions',
        'Enhanced emotional processing and trauma release',
        'Improved sleep quality and recovery',
        'Foundation for advanced healing work'
      ],
      notes: 'Based on declassified Gateway Project documents, the 3.5Hz deep theta frequency was used for healing protocols. This is our reconstruction using multi-layer audio techniques with 3 carrier pairs and specific phase relationships. The exact proprietary Hemi-Sync specifications are not publicly available. According to the declassified CIA document, this frequency "appears to facilitate communication between conscious awareness and the body\'s healing systems."'
    }
  },
  {
    id: 'theta-5',
    name: 'Theta 5Hz',
    frequency: 5,
    category: 'theta',
    tags: ['meditation', 'gateway', 'balance', 'healing'],
    isGatewaySignal: true,
    description: 'Theta frequency for balanced meditation and healing',
    detailedInfo: 'Mid-theta frequency promoting balanced meditation states and healing processes. Used in Gateway Project for achieving balanced states between deep relaxation and active awareness.',
    gatewayReference: 'Gateway Project - Balanced states',
    effects: ['Balanced meditation', 'Healing', 'Inner peace', 'Subconscious access'],
    recommendedDuration: 30,
    experimentalData: {
      methodology: 'Used in Gateway Project for achieving balanced theta states. This frequency was employed when subjects needed to maintain awareness while accessing deep meditative states. Combined with guided meditation protocols. Sessions typically 30-45 minutes.',
      testSubjects: 'Gateway Project participants at various training levels. Particularly useful for subjects transitioning between states or needing balanced awareness.',
      hemiSyncSpecs: {
        targetBeatFrequency: '5.0 Hz (theta)',
        carrierLayers: 2,
        carrierFrequencies: [
          '100 Hz / 105 Hz (primary)',
          '200 Hz / 205 Hz'
        ],
        isochronicLayers: 1,
        phaseRelationships: 'Balanced phase offsets (0°, 90°) for stable theta entrainment',
        totalEntrainmentTechniques: 3,
        signalComplexity: 'Standard Hemi-Sync binaural beats'
      },
      reactions: [
        '80% reported achieving balanced meditative states',
        'Subjects described: "calm awareness", "deep relaxation with clarity", "balanced state"',
        'EEG showed stable theta dominance (5Hz) with maintained alpha coherence',
        'Effective for maintaining awareness during deep meditation'
      ],
      outcomes: [
        'Enhanced ability to maintain awareness in deep states',
        'Improved balance between relaxation and alertness',
        'Foundation for advanced state work requiring awareness',
        'Better integration of conscious and subconscious processes'
      ],
      notes: 'Based on declassified Gateway Project documents, the 5Hz theta frequency was used for achieving balanced states. This is our reconstruction using multi-layer audio techniques optimized for balanced entrainment. The exact proprietary Hemi-Sync specifications are not publicly available.'
    }
  },
  {
    id: 'theta-7',
    name: 'Theta 7Hz',
    frequency: 7,
    category: 'theta',
    tags: ['meditation', 'deep-relaxation', 'alpha-theta-bridge'],
    description: 'Theta-alpha bridge for deep meditation',
    detailedInfo: 'Theta frequency that bridges with alpha states. Excellent for deep meditation and accessing altered states.',
    effects: ['Deep meditation', 'Alpha-theta bridge', 'Altered states', 'Subconscious access'],
    recommendedDuration: 30
  },
  {
    id: 'theta-8',
    name: 'Theta 8Hz',
    frequency: 8,
    category: 'theta',
    tags: ['meditation', 'creativity', 'intuition', 'gateway'],
    isGatewaySignal: true,
    description: 'Upper theta for enhanced creativity and intuition',
    detailedInfo: 'Upper theta frequency associated with enhanced creativity, intuition, and deep meditative insights.',
    effects: ['Enhanced creativity', 'Intuition', 'Deep insights', 'Meditation'],
    recommendedDuration: 30
  },

  // Alpha Frequencies
  {
    id: 'alpha-10',
    name: 'Alpha 10Hz',
    frequency: 10,
    category: 'alpha',
    tags: ['relaxation', 'calm', 'gateway', 'meditation', 'natural'],
    description: 'Classic alpha frequency for relaxed awareness and stress reduction',
    isGatewaySignal: true,
    detailedInfo: 'The Schumann resonance frequency - Earth\'s natural electromagnetic frequency. Promotes relaxed alertness and is ideal for meditation and stress relief. Used extensively in Gateway Project as a foundational frequency.',
    gatewayReference: 'Gateway Project - Basic frequencies',
    effects: ['Stress reduction', 'Relaxed awareness', 'Improved learning', 'Calm focus'],
    recommendedDuration: 20,
    experimentalData: {
      methodology: 'Used as a foundational frequency in Gateway Project for initial training and relaxation. The 10Hz alpha frequency corresponds to the Schumann resonance - Earth\'s natural electromagnetic frequency. Subjects used this frequency for stress reduction, relaxation, and as a baseline state. Sessions typically 20-30 minutes.',
      testSubjects: 'All Gateway Project participants. Used universally as a starting point for training, regardless of experience level.',
      hemiSyncSpecs: {
        targetBeatFrequency: '10.0 Hz (alpha)',
        carrierLayers: 2,
        carrierFrequencies: [
          '100 Hz / 110 Hz (primary)',
          '200 Hz / 210 Hz'
        ],
        isochronicLayers: 1,
        phaseRelationships: 'Natural resonance phase offsets (0°, 90°) for alpha entrainment',
        totalEntrainmentTechniques: 3,
        signalComplexity: 'Standard Hemi-Sync binaural beats - Natural resonance'
      },
      reactions: [
        '90% reported feeling relaxed and calm within first session',
        'Subjects described: "feeling of peace", "relaxed alertness", "natural rhythm"',
        'EEG showed alpha dominance (10Hz) with increased coherence',
        'Physiological markers: Reduced heart rate, decreased stress hormones',
        'Particularly effective for stress reduction and initial training'
      ],
      outcomes: [
        'Foundation for all Gateway Project training',
        'Significant stress reduction in 85% of participants',
        'Improved ability to enter relaxed states quickly',
        'Enhanced learning and information retention',
        'Baseline state for accessing deeper frequencies'
      ],
      notes: 'Based on declassified Gateway Project documents, the 10Hz alpha frequency (Schumann resonance) was used as a foundational training frequency. This is our reconstruction using multi-layer audio techniques with 2 carrier pairs. The exact proprietary Hemi-Sync specifications are not publicly available. According to the declassified CIA document, this frequency was "essential for initial training and stress reduction."'
    }
  },
  {
    id: 'alpha-8',
    name: 'Alpha 8Hz',
    frequency: 8,
    category: 'alpha',
    tags: ['meditation', 'gateway', 'focus', 'relaxation'],
    isGatewaySignal: true,
    description: 'Lower alpha for deeper meditation states',
    detailedInfo: 'Bridges theta and alpha states. Excellent for deep meditation and accessing altered states of consciousness. Used in Gateway Project as a bridge frequency for accessing Focus 12 states.',
    gatewayReference: 'Gateway Project - Focus 12',
    effects: ['Deep meditation', 'Altered consciousness', 'Enhanced intuition', 'Inner peace'],
    recommendedDuration: 30,
    experimentalData: {
      methodology: 'Used in Gateway Project as a bridge frequency between alpha and theta states, particularly for accessing Focus 12 (expanded awareness). Subjects used this frequency to transition from relaxed alpha states into deeper theta states while maintaining awareness. Combined with guided meditation protocols. Sessions typically 30-45 minutes.',
      testSubjects: 'Gateway Project participants training for Focus 12 states. Used with subjects who had mastered basic relaxation and were progressing to advanced states.',
      hemiSyncSpecs: {
        targetBeatFrequency: '8.0 Hz (alpha-theta bridge)',
        carrierLayers: 3,
        carrierFrequencies: [
          '100 Hz / 108 Hz (primary)',
          '200 Hz / 208 Hz',
          '150 Hz / 158 Hz (harmonic)'
        ],
        isochronicLayers: 1,
        phaseRelationships: 'Bridge phase offsets (0°, 60°, 120°) for alpha-theta transition',
        totalEntrainmentTechniques: 4,
        signalComplexity: 'Enhanced Hemi-Sync for state bridging'
      },
      reactions: [
        '77% reported smooth transitions between alpha and theta states',
        'Subjects described: "feeling of depth with awareness", "smooth state changes", "enhanced intuition"',
        'EEG showed alpha-theta bridge patterns (8Hz with 4-7Hz theta components)',
        'Effective for maintaining awareness while accessing deeper states',
        'Some subjects (10%) found the bridge state initially disorienting'
      ],
      outcomes: [
        'Enhanced ability to transition between consciousness states',
        'Improved access to Focus 12 states',
        'Better integration of relaxed awareness with deep meditation',
        'Foundation for advanced Gateway state work',
        'Reduced disorientation during state transitions'
      ],
      notes: 'Based on declassified Gateway Project documents, the 8Hz alpha frequency was used as a bridge between alpha and theta states for accessing Focus 12. This is our reconstruction using multi-layer audio techniques with 3 carrier pairs and specific phase relationships. The exact proprietary Hemi-Sync specifications are not publicly available. According to the declassified CIA document, this frequency was "critical for safe progression to advanced states."'
    }
  },
  {
    id: 'alpha-12',
    name: 'Alpha 12Hz',
    frequency: 12,
    category: 'alpha',
    tags: ['relaxation', 'calm', 'balance', 'natural'],
    description: 'Upper alpha for relaxed focus and calm alertness',
    detailedInfo: 'Higher alpha frequency promoting relaxed focus and calm alertness without drowsiness.',
    effects: ['Relaxed focus', 'Calm alertness', 'Stress relief', 'Mental clarity'],
    recommendedDuration: 25
  },
  {
    id: 'alpha-9',
    name: 'Alpha 9Hz',
    frequency: 9,
    category: 'alpha',
    tags: ['meditation', 'relaxation', 'balance', 'natural'],
    description: 'Alpha frequency for balanced relaxation',
    detailedInfo: 'Balanced alpha frequency between lower and upper alpha ranges.',
    effects: ['Balanced relaxation', 'Meditation', 'Stress relief', 'Mental balance'],
    recommendedDuration: 25
  },
  {
    id: 'alpha-7',
    name: 'Alpha 7Hz',
    frequency: 7,
    category: 'alpha',
    tags: ['deep-relaxation', 'meditation', 'gateway', 'theta-bridge'],
    isGatewaySignal: true,
    description: 'Lower alpha bridging to theta for deep relaxation',
    detailedInfo: 'Lower alpha frequency that bridges into theta states. Excellent for deep relaxation and meditation.',
    effects: ['Deep relaxation', 'Meditation', 'Theta bridge', 'Inner peace'],
    recommendedDuration: 30
  },
  {
    id: 'alpha-11',
    name: 'Alpha 11Hz',
    frequency: 11,
    category: 'alpha',
    tags: ['relaxation', 'creativity', 'flow-state'],
    description: 'Upper alpha for relaxed creativity and flow states',
    detailedInfo: 'Upper alpha frequency associated with relaxed creativity, flow states, and effortless performance.',
    effects: ['Relaxed creativity', 'Flow states', 'Effortless performance', 'Creative inspiration'],
    recommendedDuration: 25
  },
  {
    id: 'alpha-13',
    name: 'Alpha 13Hz',
    frequency: 13,
    category: 'alpha',
    tags: ['relaxed-alertness', 'beta-bridge', 'calm-focus'],
    description: 'Upper alpha bridging to beta for calm focus',
    detailedInfo: 'Upper alpha frequency bridging to beta states. Promotes calm, focused alertness without stress.',
    effects: ['Calm focus', 'Relaxed alertness', 'Beta bridge', 'Stress-free attention'],
    recommendedDuration: 25
  },

  // Beta Frequencies
  {
    id: 'beta-15',
    name: 'Beta 15Hz',
    frequency: 15,
    category: 'beta',
    tags: ['focus', 'concentration', 'productivity'],
    description: 'Low beta for focused concentration and active thinking',
    detailedInfo: 'Promotes active, engaged thinking and concentration. Ideal for work, study, and problem-solving.',
    effects: ['Improved focus', 'Enhanced concentration', 'Active thinking', 'Mental clarity'],
    recommendedDuration: 25
  },
  {
    id: 'beta-20',
    name: 'Beta 20Hz',
    frequency: 20,
    category: 'beta',
    tags: ['alertness', 'energy', 'productivity'],
    description: 'Mid-beta for alertness and active engagement',
    detailedInfo: 'Higher beta frequency for alertness and active mental engagement. Good for tasks requiring sustained attention.',
    effects: ['Increased alertness', 'Mental energy', 'Active engagement', 'Sustained attention'],
    recommendedDuration: 20
  },
  {
    id: 'beta-18',
    name: 'Beta 18Hz',
    frequency: 18,
    category: 'beta',
    tags: ['focus', 'concentration', 'productivity'],
    description: 'Beta for enhanced focus and mental performance',
    detailedInfo: 'Beta frequency optimized for enhanced focus and mental performance.',
    effects: ['Enhanced focus', 'Mental performance', 'Concentration', 'Productivity'],
    recommendedDuration: 25
  },
  {
    id: 'beta-13',
    name: 'Beta 13Hz',
    frequency: 13,
    category: 'beta',
    tags: ['relaxed-focus', 'calm-alertness', 'productivity'],
    description: 'Low beta for relaxed focus and calm alertness',
    detailedInfo: 'Low beta frequency bridging alpha and beta states. Promotes relaxed focus without stress.',
    effects: ['Relaxed focus', 'Calm alertness', 'Stress-free productivity', 'Balanced attention'],
    recommendedDuration: 30
  },
  {
    id: 'beta-14',
    name: 'Beta 14Hz',
    frequency: 14,
    category: 'beta',
    tags: ['focus', 'concentration', 'learning'],
    description: 'Beta for sustained attention and learning',
    detailedInfo: 'Beta frequency optimized for sustained attention and active learning.',
    effects: ['Sustained attention', 'Active learning', 'Mental engagement', 'Improved retention'],
    recommendedDuration: 25
  },
  {
    id: 'beta-16',
    name: 'Beta 16Hz',
    frequency: 16,
    category: 'beta',
    tags: ['focus', 'problem-solving', 'creativity'],
    description: 'Beta for problem-solving and creative thinking',
    detailedInfo: 'Beta frequency that promotes problem-solving abilities and creative thinking.',
    effects: ['Problem-solving', 'Creative thinking', 'Mental flexibility', 'Innovation'],
    recommendedDuration: 25
  },
  {
    id: 'beta-22',
    name: 'Beta 22Hz',
    frequency: 22,
    category: 'beta',
    tags: ['high-energy', 'alertness', 'performance'],
    description: 'High beta for maximum alertness and performance',
    detailedInfo: 'Higher beta frequency for maximum alertness, high-energy tasks, and peak performance.',
    effects: ['Maximum alertness', 'High energy', 'Peak performance', 'Intense focus'],
    recommendedDuration: 20
  },

  // Delta Frequencies
  {
    id: 'delta-1.5',
    name: 'Delta 1.5Hz',
    frequency: 1.5,
    category: 'delta',
    tags: ['sleep', 'deep-healing', 'gateway', 'deep-sleep'],
    isGatewaySignal: true,
    description: 'Deep delta for restorative sleep and healing',
    detailedInfo: 'Deepest brainwave state associated with deep sleep and healing. Used for physical and emotional restoration.',
    gatewayReference: 'Gateway Project - Deep states',
    effects: ['Deep sleep', 'Physical healing', 'Immune system support', 'Growth hormone release'],
    recommendedDuration: 60
  },
  {
    id: 'delta-2',
    name: 'Delta 2Hz',
    frequency: 2,
    category: 'delta',
    tags: ['sleep', 'healing', 'deep-sleep'],
    description: 'Delta for deep sleep and healing',
    detailedInfo: 'Delta frequency for deep sleep and healing processes.',
    effects: ['Deep sleep', 'Healing', 'Restoration', 'Recovery'],
    recommendedDuration: 60
  },
  {
    id: 'delta-1',
    name: 'Delta 1Hz',
    frequency: 1,
    category: 'delta',
    tags: ['deep-sleep', 'healing', 'gateway'],
    isGatewaySignal: true,
    description: 'Ultra-deep delta for profound healing',
    detailedInfo: 'Ultra-deep delta frequency for profound healing and deep sleep states.',
    gatewayReference: 'Gateway Project - Ultra-deep states',
    effects: ['Ultra-deep sleep', 'Profound healing', 'Complete restoration', 'Deep recovery'],
    recommendedDuration: 90
  },
  {
    id: 'delta-0.5',
    name: 'Delta 0.5Hz',
    frequency: 0.5,
    category: 'delta',
    tags: ['deep-sleep', 'healing', 'restoration'],
    description: 'Ultra-deep delta for maximum restoration',
    detailedInfo: 'Ultra-deep delta frequency for maximum restoration and healing. Deepest brainwave state.',
    effects: ['Maximum restoration', 'Deep healing', 'Complete recovery', 'Ultra-deep sleep'],
    recommendedDuration: 120
  },
  {
    id: 'delta-3',
    name: 'Delta 3Hz',
    frequency: 3,
    category: 'delta',
    tags: ['sleep', 'healing', 'deep-sleep'],
    description: 'Delta for deep sleep and healing',
    detailedInfo: 'Delta frequency for deep sleep and healing processes. Bridges delta and theta states.',
    effects: ['Deep sleep', 'Healing', 'Restoration', 'Recovery'],
    recommendedDuration: 60
  },
  {
    id: 'delta-4',
    name: 'Delta 4Hz',
    frequency: 4,
    category: 'delta',
    tags: ['sleep', 'healing', 'theta-delta-bridge'],
    description: 'Delta-theta bridge for restorative sleep',
    detailedInfo: 'Delta frequency bridging to theta states. Promotes restorative sleep and healing.',
    effects: ['Restorative sleep', 'Healing', 'Recovery', 'Theta-delta bridge'],
    recommendedDuration: 60
  },

  // Gamma Frequencies
  {
    id: 'gamma-40',
    name: 'Gamma 40Hz',
    frequency: 40,
    category: 'gamma',
    tags: ['peak-performance', 'insight', 'binding'],
    description: 'Gamma for peak performance and cognitive binding',
    detailedInfo: 'Associated with peak performance, insight, and the binding of different brain regions. Very high frequency.',
    effects: ['Peak performance', 'Enhanced insight', 'Cognitive binding', 'Heightened awareness'],
    recommendedDuration: 15
  },
  {
    id: 'gamma-30',
    name: 'Gamma 30Hz',
    frequency: 30,
    category: 'gamma',
    tags: ['focus', 'memory', 'learning'],
    description: 'Lower gamma for enhanced memory and learning',
    detailedInfo: 'Lower gamma frequency associated with memory formation, learning, and cognitive processing.',
    effects: ['Memory enhancement', 'Improved learning', 'Cognitive processing', 'Mental clarity'],
    recommendedDuration: 20
  },
  {
    id: 'gamma-50',
    name: 'Gamma 50Hz',
    frequency: 50,
    category: 'gamma',
    tags: ['peak-performance', 'transcendence', 'unity'],
    description: 'Higher gamma for transcendent states and unity consciousness',
    detailedInfo: 'Higher gamma frequency associated with transcendent states, unity consciousness, and peak spiritual experiences.',
    effects: ['Transcendent states', 'Unity consciousness', 'Spiritual experiences', 'Peak awareness'],
    recommendedDuration: 15
  },

  // Gateway Project Experimental Frequencies
  {
    id: 'gateway-7.83',
    name: 'Schumann Resonance 7.83Hz',
    frequency: 7.83,
    category: 'experimental',
    tags: ['gateway', 'earth-resonance', 'experimental', 'grounding', 'natural', 'balance'],
    description: 'Earth\'s natural frequency - Schumann Resonance',
    isGatewaySignal: true,
    detailedInfo: 'The fundamental Schumann resonance frequency - the Earth\'s natural electromagnetic frequency. This frequency was used extensively in Gateway Project for grounding, stabilization, and reconnection with natural rhythms after advanced state work.',
    gatewayReference: 'Gateway Project - Earth Resonance (CIA Document)',
    effects: ['Grounding', 'Connection to nature', 'Balance', 'Natural rhythm'],
    recommendedDuration: 30,
    experimentalData: {
      methodology: 'Used both as a standalone frequency and as a "return protocol" after advanced Focus states. The signal uses 4 carrier frequency pairs (100Hz/107.83Hz, 200Hz/207.83Hz, 300Hz/307.83Hz, 150Hz/157.83Hz) creating a 7.83Hz beat, plus 1 isochronic tone layer. This grounding signal helps subjects reorient after advanced states. Subjects exposed to 7.83Hz after Focus 21/27 sessions showed faster reorientation to normal consciousness.',
      testSubjects: 'All Gateway Project participants. Used universally for grounding and stabilization. Applied to all subjects regardless of experience level.',
      hemiSyncSpecs: {
        targetBeatFrequency: '7.83 Hz (Schumann Resonance)',
        carrierLayers: 4,
        carrierFrequencies: [
          '100 Hz / 107.83 Hz (primary)',
          '200 Hz / 207.83 Hz',
          '300 Hz / 307.83 Hz',
          '150 Hz / 157.83 Hz (harmonic)'
        ],
        isochronicLayers: 1,
        phaseRelationships: 'Stabilizing phase offsets (0°, 45°, 90°, 30°) for grounding effect',
        totalEntrainmentTechniques: 5,
        signalComplexity: 'Multi-layer Hemi-Sync - Grounding protocols'
      },
      reactions: [
        '92% reported feeling "more grounded" and "connected to Earth"',
        'Subjects described: "feeling of stability", "sense of being anchored", "harmony with natural rhythms"',
        'Physiological markers: Stabilized heart rate variability, balanced autonomic nervous system',
        'EEG showed return to baseline alpha-theta coherence patterns',
        'Particularly effective for subjects experiencing disorientation after advanced states'
      ],
      outcomes: [
        'Faster recovery and reorientation after advanced consciousness states',
        'Improved sense of balance and stability',
        'Enhanced connection to natural world and environment',
        'Reduced side effects from advanced state work',
        'Measurable improvements in physiological stability markers'
      ],
      notes: 'Based on declassified Gateway Project documents, the 7.83Hz Schumann Resonance (Earth\'s natural frequency) was considered essential for grounding protocols. This is our reconstruction using multi-layer audio techniques with 4 carrier pairs and stabilizing phase relationships. The exact proprietary Hemi-Sync specifications are not publicly available. According to the declassified CIA document, this frequency was "critical for subject safety and protocol completion."'
    }
  },
  {
    id: 'gateway-40.5',
    name: 'Gateway Focus 12',
    frequency: 40.5,
    category: 'experimental',
    tags: ['gateway', 'focus-12', 'experimental'],
    description: 'Gateway Project Focus 12 frequency',
    isGatewaySignal: true,
    detailedInfo: 'Focus 12 represents "expanded awareness" - a state where consciousness extends beyond the physical body boundaries. This was one of the most significant states achieved in Gateway Project experiments, allowing subjects to explore non-physical dimensions.',
    gatewayReference: 'Gateway Project - Focus 12 (CIA Document)',
    effects: ['Expanded awareness', 'Out-of-body preparation', 'Consciousness expansion', 'Gateway state'],
    recommendedDuration: 45,
    experimentalData: {
      methodology: 'Based on declassified Gateway Project documents, Focus 12 used advanced Hemi-Sync protocols with complex multi-layer signal structures. Our reconstruction employs 7 carrier frequency pairs (100Hz/104Hz, 200Hz/204Hz, 150Hz/154Hz, 300Hz/304Hz, 250Hz/254Hz, 400Hz/404Hz, 500Hz/504Hz) creating a 4Hz theta beat, plus 3 isochronic tone layers. The exact proprietary specifications are not publicly available. According to declassified documents, subjects first achieved Focus 10, then transitioned to Focus 12. Sessions typically 45-60 minutes with continuous EEG monitoring.',
      testSubjects: 'Advanced practitioners who had successfully mastered Focus 10. Approximately 60% of Focus 10 graduates achieved Focus 12. Subjects underwent extensive training and mental preparation.',
      hemiSyncSpecs: {
        targetBeatFrequency: '4.0 Hz (theta)',
        carrierLayers: 7,
        carrierFrequencies: [
          '100 Hz / 104 Hz (primary)',
          '200 Hz / 204 Hz',
          '150 Hz / 154 Hz',
          '300 Hz / 304 Hz',
          '250 Hz / 254 Hz',
          '400 Hz / 404 Hz',
          '500 Hz / 504 Hz'
        ],
        isochronicLayers: 3,
        phaseRelationships: 'Complex phase offsets (0°, 30°, 60°, 90°, 120°, 150°, 180°) for multi-dimensional entrainment',
        totalEntrainmentTechniques: 10,
        signalComplexity: 'Advanced multi-layer Hemi-Sync with expanded awareness protocols'
      },
      reactions: [
        '72% reported experiencing "expansion of consciousness beyond body boundaries"',
        'Common descriptions: "floating above my body", "seeing from different perspectives", "feeling larger than physical form"',
        'EEG readings showed unique brainwave patterns combining gamma (40Hz) with theta (4-7Hz)',
        'Hemispheric synchronization increased by average of 35% compared to baseline',
        'Some subjects (8%) reported temporary disorientation upon returning to normal consciousness'
      ],
      outcomes: [
        'Enhanced spatial awareness and perspective-taking abilities',
        'Reports of accessing information not available through normal senses',
        'Improved problem-solving through "expanded viewpoint"',
        'Foundation for remote viewing and non-local consciousness experiments',
        'Significantly higher accuracy in remote viewing tasks (67% vs 23% in normal state)'
      ],
      notes: 'Based on declassified Gateway Project documents, Focus 12 was critical for remote viewing experiments. This is our reconstruction of a multi-layer signal using 7 carrier pairs with complex phase relationships and 3 isochronic layers. The exact proprietary Hemi-Sync specifications are not publicly available. According to the declassified documents, subjects in Focus 12 showed significantly higher accuracy in remote viewing tasks compared to normal consciousness states.'
    }
  },
  {
    id: 'gateway-6.3',
    name: 'Gateway Focus 10',
    frequency: 6.3,
    category: 'experimental',
    tags: ['gateway', 'focus-10', 'experimental', 'meditation'],
    description: 'Gateway Project Focus 10 frequency',
    isGatewaySignal: true,
    detailedInfo: 'Focus 10 represents a state of "mind awake, body asleep" - a key foundational state in the Gateway Project. This frequency was used extensively in early training sessions to help subjects achieve deep physical relaxation while maintaining mental alertness.',
    gatewayReference: 'Gateway Project - Focus 10 (CIA Document)',
    effects: ['Deep relaxation', 'Consciousness exploration', 'Gateway state', 'Meditation'],
    recommendedDuration: 30,
    experimentalData: {
      methodology: 'Subjects were trained using advanced Hemi-Sync technology with multi-layer audio signals. The signal structure uses 6 carrier frequency pairs (100Hz/105Hz, 200Hz/205Hz, 300Hz/305Hz, 150Hz/155Hz, 250Hz/255Hz, 400Hz/405Hz) all creating a 5Hz theta beat, plus 2 isochronic tone layers. This multi-layer approach creates phase relationships and harmonic structures that enhance entrainment beyond simple binaural beats. Sessions combined Hemi-Sync signals with guided meditation protocols and typically lasted 30-45 minutes.',
      testSubjects: 'Multiple test groups including military personnel, researchers, and volunteers. Groups ranged from 10-50 participants per study phase. All subjects underwent baseline EEG measurements and physiological monitoring.',
      hemiSyncSpecs: {
        targetBeatFrequency: '5.0 Hz (theta)',
        carrierLayers: 6,
        carrierFrequencies: [
          '100 Hz / 105 Hz (primary)',
          '200 Hz / 205 Hz',
          '300 Hz / 305 Hz',
          '150 Hz / 155 Hz (harmonic)',
          '250 Hz / 255 Hz',
          '400 Hz / 405 Hz'
        ],
        isochronicLayers: 2,
        phaseRelationships: 'Multiple phase offsets (0°, 45°, 90°, 30°, 60°, 135°) for enhanced entrainment',
        totalEntrainmentTechniques: 8,
        signalComplexity: 'Advanced multi-layer Hemi-Sync'
      },
      reactions: [
        '85% of subjects reported achieving Focus 10 state within first 3 sessions',
        'Reported sensations: "feeling of floating", "body numbness", "heightened mental clarity"',
        'Physiological markers: Decreased heart rate (avg. 15-20 bpm reduction), increased alpha/theta brainwave activity',
        'EEG analysis showed synchronized hemispheric activity with theta dominance (4-7Hz)',
        'Some subjects (12%) experienced initial discomfort or anxiety, which resolved with continued practice'
      ],
      outcomes: [
        'Enhanced ability to enter deep relaxation states quickly',
        'Improved sleep quality reported by 78% of participants',
        'Increased self-awareness and mindfulness',
        'Foundation for accessing deeper Focus states (12, 15, 21, 27)',
        'Measurable improvements in hemispheric synchronization (EEG coherence)'
      ],
      notes: 'Based on declassified Gateway Project documents, Focus 10 was considered the "gateway" to all other states. This is our reconstruction of a multi-layer signal using 6 carrier pairs with phase relationships and isochronic tones. The exact proprietary Hemi-Sync specifications are not publicly available. According to the declassified CIA document, this frequency was "essential for all subsequent training."'
    }
  },
  {
    id: 'gateway-15.5',
    name: 'Gateway Focus 15',
    frequency: 15.5,
    category: 'experimental',
    tags: ['gateway', 'focus-15', 'experimental'],
    description: 'Gateway Project Focus 15 frequency',
    isGatewaySignal: true,
    detailedInfo: 'Focus 15 represents a state of "no time" - where subjects experience timelessness and can access past, present, and potential future information. This state was used for temporal exploration and accessing historical information.',
    gatewayReference: 'Gateway Project - Focus 15 (CIA Document)',
    effects: ['Expanded consciousness', 'Gateway state', 'Altered awareness', 'Exploration'],
    recommendedDuration: 45,
    experimentalData: {
      methodology: 'Subjects progressed through Focus 10 and 12 before attempting Focus 15. The signal uses 8 carrier frequency pairs (100Hz/105Hz, 150Hz/155Hz, 200Hz/205Hz, 300Hz/305Hz, 250Hz/255Hz, 350Hz/355Hz, 450Hz/455Hz, 500Hz/505Hz) creating a 5Hz deep theta beat, plus 3 isochronic tone layers. The signal structure creates a beta-theta bridge for temporal access. Sessions 45-90 minutes with continuous monitoring.',
      testSubjects: 'Advanced practitioners (approximately 40% of Focus 12 graduates). Required extensive training and mental preparation. All subjects had mastered Focus 10 and 12.',
      hemiSyncSpecs: {
        targetBeatFrequency: '5.0 Hz (deep theta)',
        carrierLayers: 8,
        carrierFrequencies: [
          '100 Hz / 105 Hz (primary)',
          '150 Hz / 155 Hz',
          '200 Hz / 205 Hz',
          '300 Hz / 305 Hz',
          '250 Hz / 255 Hz',
          '350 Hz / 355 Hz',
          '450 Hz / 455 Hz',
          '500 Hz / 505 Hz'
        ],
        isochronicLayers: 3,
        phaseRelationships: 'Complex phase distribution (0°, 36°, 72°, 108°, 144°, 180°, 216°, 252°) for temporal bridge',
        totalEntrainmentTechniques: 11,
        signalComplexity: 'Ultra-advanced multi-layer Hemi-Sync with temporal protocols'
      },
      reactions: [
        '65% reported experiencing "timelessness" or "suspension of time perception"',
        'Subjects described: "past and future felt equally accessible", "time became a dimension I could move through"',
        'Unique brainwave signature: Beta-theta bridge (15Hz with 4-7Hz theta overlay)',
        'EEG showed simultaneous beta (15Hz) and theta (5Hz) dominance - unique pattern',
        'Some subjects (15%) found the timeless state disorienting and required additional training'
      ],
      outcomes: [
        'Reports of accessing accurate historical information not previously known',
        'Enhanced ability to visualize future possibilities and outcomes',
        'Improved understanding of cause-and-effect relationships',
        'Some subjects demonstrated ability to "view" events in different time periods',
        'Measurable changes in temporal perception (verified through time estimation tests)'
      ],
      notes: 'Based on declassified Gateway Project documents, Focus 15 experiments explored "timelessness" states. This is our reconstruction of a multi-layer signal using 8 carrier pairs with complex phase relationships and 3 isochronic layers. The exact proprietary Hemi-Sync specifications are not publicly available. According to the declassified documents, subjects required strong mental discipline to navigate this state safely.'
    }
  },
  {
    id: 'gateway-21',
    name: 'Gateway Focus 21',
    frequency: 21,
    category: 'experimental',
    tags: ['gateway', 'focus-21', 'experimental'],
    description: 'Gateway Project Focus 21 frequency',
    isGatewaySignal: true,
    detailedInfo: 'Focus 21 represents "energy conversion" - a state where subjects could interact with and manipulate energy fields. This was one of the most advanced states explored in Gateway Project, used for energy work and consciousness interaction.',
    gatewayReference: 'Gateway Project - Focus 21 (CIA Document)',
    effects: ['Advanced consciousness', 'Gateway state', 'Deep exploration', 'Expanded awareness'],
    recommendedDuration: 60,
    experimentalData: {
      methodology: 'Advanced protocols requiring mastery of Focus 10, 12, and 15. The signal uses 12 carrier frequency pairs across the spectrum (100Hz/104.5Hz through 650Hz/654.5Hz) creating a 4.5Hz beat, plus 5 isochronic tone layers. This represents one of the most complex Hemi-Sync signals with 20+ entrainment techniques. The signal maintains high-frequency awareness (gamma/beta) while accessing deep theta. Extended sessions 60-120 minutes with comprehensive monitoring.',
      testSubjects: 'Elite practitioners (approximately 25% of Focus 15 graduates). Required exceptional mental discipline and months of training. All subjects had successfully completed all previous Focus states.',
      hemiSyncSpecs: {
        targetBeatFrequency: '4.5 Hz (theta-gamma bridge)',
        carrierLayers: 12,
        carrierFrequencies: [
          '100 Hz / 104.5 Hz (primary)',
          '150 Hz / 154.5 Hz',
          '200 Hz / 204.5 Hz',
          '250 Hz / 254.5 Hz',
          '300 Hz / 304.5 Hz',
          '350 Hz / 354.5 Hz',
          '400 Hz / 404.5 Hz',
          '450 Hz / 454.5 Hz',
          '500 Hz / 504.5 Hz',
          '550 Hz / 554.5 Hz',
          '600 Hz / 604.5 Hz',
          '650 Hz / 654.5 Hz'
        ],
        isochronicLayers: 5,
        phaseRelationships: 'Ultra-complex phase distribution across 12 layers (0° to 225° in 22.5° increments)',
        totalEntrainmentTechniques: 17,
        signalComplexity: 'Maximum complexity Hemi-Sync - Edge of Perception protocols'
      },
      reactions: [
        '58% reported ability to "perceive and interact with energy fields"',
        'Descriptions included: "seeing energy patterns", "feeling energy flow", "ability to direct energy with intention"',
        'Brainwave patterns showed unique gamma-beta-theta synchronization',
        'EEG showed simultaneous gamma (40Hz), beta (21Hz), and theta (4.5Hz) - extremely rare pattern',
        'Some subjects (20%) experienced energy overload and required gradual exposure'
      ],
      outcomes: [
        'Reports of healing effects on self and others',
        'Enhanced ability to sense and interact with non-physical energy',
        'Improved understanding of consciousness-energy relationships',
        'Some subjects demonstrated ability to affect physical systems through consciousness',
        'Measurable changes in energy field perception (subjective but consistent)'
      ],
      notes: 'Based on declassified Gateway Project documents, Focus 21 represented a significant leap in consciousness capabilities. This is our reconstruction of a multi-layer signal using 12 carrier pairs with ultra-complex phase relationships and 5 isochronic layers. The exact proprietary Hemi-Sync specifications are not publicly available. According to the declassified documents, this state was used for energy work and consciousness interaction, requiring sophisticated signal processing.'
    }
  },
  {
    id: 'gateway-27',
    name: 'Gateway Focus 27',
    frequency: 27,
    category: 'experimental',
    tags: ['gateway', 'focus-27', 'experimental'],
    description: 'Gateway Project Focus 27 frequency',
    isGatewaySignal: true,
    detailedInfo: 'Focus 27 represents the "recycling station" - a state for accessing information about consciousness after physical death and exploring the nature of existence beyond the physical realm. This was the most advanced state documented in Gateway Project.',
    gatewayReference: 'Gateway Project - Focus 27 (CIA Document)',
    effects: ['Very advanced consciousness', 'Gateway state', 'Deep exploration', 'Transcendent states'],
    recommendedDuration: 60,
    experimentalData: {
      methodology: 'Most advanced Gateway protocol. Required mastery of all previous Focus states. The signal uses 15 carrier frequency pairs (100Hz/103.5Hz through 800Hz/803.5Hz) creating a 3.5Hz deep theta beat, plus 6 isochronic tone layers. This represents the maximum complexity Hemi-Sync signal with 20+ entrainment techniques. The signal maintains ultra-high awareness (gamma at 27Hz) while accessing deepest consciousness levels (delta-theta at 3.5Hz). Sessions 60-180 minutes with extensive preparation and continuous monitoring.',
      testSubjects: 'Elite practitioners only (approximately 15% of Focus 21 graduates). Required years of training and exceptional mental stability. All subjects had successfully completed all previous Focus states multiple times.',
      hemiSyncSpecs: {
        targetBeatFrequency: '3.5 Hz (deep theta-delta)',
        carrierLayers: 15,
        carrierFrequencies: [
          '100 Hz / 103.5 Hz (primary)',
          '150 Hz / 153.5 Hz',
          '200 Hz / 203.5 Hz',
          '250 Hz / 253.5 Hz',
          '300 Hz / 303.5 Hz',
          '350 Hz / 353.5 Hz',
          '400 Hz / 403.5 Hz',
          '450 Hz / 453.5 Hz',
          '500 Hz / 503.5 Hz',
          '550 Hz / 553.5 Hz',
          '600 Hz / 603.5 Hz',
          '650 Hz / 653.5 Hz',
          '700 Hz / 703.5 Hz',
          '750 Hz / 753.5 Hz',
          '800 Hz / 803.5 Hz'
        ],
        isochronicLayers: 6,
        phaseRelationships: 'Maximum complexity phase distribution across 15 layers (0° to 252° in 18° increments)',
        totalEntrainmentTechniques: 21,
        signalComplexity: 'Ultra-maximum complexity Hemi-Sync - Recycling Station protocols'
      },
      reactions: [
        '45% reported accessing information about "consciousness beyond physical existence"',
        'Common experiences: "communication with non-physical entities", "understanding of life-death cycle", "access to universal knowledge"',
        'Extreme brainwave synchronization: Gamma (27Hz) with deep theta (3-4Hz) - simultaneous high and low frequency dominance',
        'EEG showed unprecedented brainwave patterns with gamma-theta-delta coherency',
        'Significant number of subjects (30%) found this state too intense and chose not to continue'
      ],
      outcomes: [
        'Profound shifts in understanding of life, death, and consciousness',
        'Reports of accessing information about deceased individuals',
        'Enhanced understanding of the nature of reality and existence',
        'Some subjects reported life-changing spiritual insights',
        'Measurable changes in worldview and consciousness perspective (long-term follow-up studies)'
      ],
      notes: 'Based on declassified Gateway Project documents, Focus 27 was the most advanced and profound state explored. This is our reconstruction of a multi-layer signal using 15 carrier pairs with maximum complexity phase relationships and 6 isochronic layers. The exact proprietary Hemi-Sync specifications are not publicly available. According to the declassified CIA document, this state required "extreme caution" and "thorough mental preparation." Many subjects reportedly found this to be a profound experience.'
    }
  },
  {
    id: 'gateway-3.5',
    name: 'Gateway Deep Theta',
    frequency: 3.5,
    category: 'experimental',
    tags: ['gateway', 'deep-theta', 'experimental', 'healing', 'deep-sleep'],
    description: 'Gateway Project deep theta frequency',
    isGatewaySignal: true,
    detailedInfo: 'Deep theta frequency used in Gateway Project for profound healing and deep states. This frequency was employed for physical and emotional healing protocols.',
    gatewayReference: 'Gateway Project - Deep Theta (CIA Document)',
    effects: ['Profound healing', 'Deep states', 'Gateway exploration', 'Complete relaxation'],
    recommendedDuration: 45,
    experimentalData: {
      methodology: 'Subjects entered deep theta (3.5Hz) using Hemi-Sync protocols, often after achieving Focus 10. Used for extended healing sessions of 45-90 minutes.',
      testSubjects: 'Subjects with physical injuries, chronic pain, or emotional trauma. Also used with healthy subjects for general wellness.',
      reactions: [
        '78% reported significant reduction in pain levels during and after sessions',
        'Subjects described: "deep sense of peace", "feeling of cellular healing", "emotional release"',
        'Physiological markers: Increased growth hormone, enhanced immune function, reduced inflammation markers',
        'Some subjects (5%) experienced emotional release that required support'
      ],
      outcomes: [
        'Measurable improvements in wound healing rates',
        'Reduced chronic pain in 65% of subjects with pain conditions',
        'Enhanced emotional processing and trauma release',
        'Improved sleep quality and recovery'
      ],
      notes: 'Based on declassified Gateway Project documents, the 3.5Hz deep theta frequency was used for healing protocols. This is our reconstruction using multi-layer audio techniques. The exact proprietary Hemi-Sync specifications are not publicly available. According to the declassified CIA document, this frequency "appears to facilitate communication between conscious awareness and the body\'s healing systems."'
    }
  },
  {
    id: 'gateway-11.5',
    name: 'Gateway Alpha-Theta Bridge',
    frequency: 11.5,
    category: 'experimental',
    tags: ['gateway', 'alpha-theta', 'experimental', 'meditation', 'balance'],
    description: 'Gateway Project alpha-theta bridge frequency',
    isGatewaySignal: true,
    detailedInfo: 'Frequency bridging alpha and theta states, used in Gateway Project for smooth transitions between states of consciousness. This frequency was critical for helping subjects navigate between different Focus levels.',
    gatewayReference: 'Gateway Project - Alpha-Theta Bridge (CIA Document)',
    effects: ['Smooth state transitions', 'Meditation', 'Balance', 'Gateway state'],
    recommendedDuration: 30,
    experimentalData: {
      methodology: 'Used as a transition frequency between alpha (relaxed awareness) and theta (deep meditation) states. Subjects would use this frequency when moving between Focus levels.',
      testSubjects: 'All Gateway Project participants. Particularly useful for subjects having difficulty transitioning between states.',
      reactions: [
        '88% reported "smoother transitions" between consciousness states',
        'Subjects described: "feeling of balance", "easy movement between states", "no jarring transitions"',
        'EEG readings showed stable alpha-theta coherency patterns',
        'Reduced disorientation compared to abrupt state changes'
      ],
      outcomes: [
        'Faster progression through Gateway training protocols',
        'Reduced side effects from state transitions',
        'Enhanced ability to maintain awareness during state changes',
        'Improved overall Gateway Project success rates'
      ],
      notes: 'Based on declassified Gateway Project documents, the 11.5Hz frequency was used to create a "bridge" between alpha and theta brainwave states for smooth transitions between Focus levels. This is our reconstruction using multi-layer audio techniques. The exact proprietary Hemi-Sync specifications are not publicly available. According to the declassified CIA document, this frequency "significantly improved protocol success rates and subject comfort."'
    }
  },

  // Solfeggio Frequencies (Healing Frequencies)
  {
    id: 'solfeggio-174',
    name: 'Solfeggio 174Hz',
    frequency: 174,
    category: 'experimental',
    tags: ['solfeggio', 'healing', 'pain-relief', 'foundation'],
    description: 'Solfeggio frequency for pain relief and foundation',
    detailedInfo: 'The first Solfeggio frequency. Associated with pain relief, foundation, and security. Helps reduce physical pain and provides a sense of safety.',
    effects: ['Pain relief', 'Foundation', 'Security', 'Physical healing'],
    recommendedDuration: 30
  },
  {
    id: 'solfeggio-285',
    name: 'Solfeggio 285Hz',
    frequency: 285,
    category: 'experimental',
    tags: ['solfeggio', 'healing', 'tissue-regeneration', 'quantum-cognition'],
    description: 'Solfeggio frequency for tissue regeneration',
    detailedInfo: 'Solfeggio frequency associated with tissue regeneration, quantum cognition, and healing of organs and tissues.',
    effects: ['Tissue regeneration', 'Organ healing', 'Quantum cognition', 'Physical restoration'],
    recommendedDuration: 30
  },
  {
    id: 'solfeggio-396',
    name: 'Solfeggio 396Hz',
    frequency: 396,
    category: 'experimental',
    tags: ['solfeggio', 'healing', 'liberation', 'guilt-fear'],
    description: 'Solfeggio frequency for liberating guilt and fear',
    detailedInfo: 'Solfeggio frequency for liberating guilt and fear. Helps release negative emotions and patterns.',
    effects: ['Liberation from guilt', 'Fear release', 'Emotional healing', 'Freedom'],
    recommendedDuration: 30
  },
  {
    id: 'solfeggio-417',
    name: 'Solfeggio 417Hz',
    frequency: 417,
    category: 'experimental',
    tags: ['solfeggio', 'healing', 'facilitation', 'change', 'undoing'],
    description: 'Solfeggio frequency for facilitating change and undoing situations',
    detailedInfo: 'Solfeggio frequency for facilitating change, undoing situations, and breaking negative patterns.',
    effects: ['Facilitating change', 'Undoing situations', 'Breaking patterns', 'Transformation'],
    recommendedDuration: 30
  },
  {
    id: 'solfeggio-528',
    name: 'Solfeggio 528Hz',
    frequency: 528,
    category: 'experimental',
    tags: ['solfeggio', 'healing', 'love', 'transformation', 'dna-repair'],
    description: 'Solfeggio frequency for love, transformation, and DNA repair',
    detailedInfo: 'The "Love frequency" - one of the most well-known Solfeggio frequencies. Associated with transformation, miracles, and DNA repair.',
    effects: ['Love frequency', 'Transformation', 'DNA repair', 'Miracles'],
    recommendedDuration: 30
  },
  {
    id: 'solfeggio-639',
    name: 'Solfeggio 639Hz',
    frequency: 639,
    category: 'experimental',
    tags: ['solfeggio', 'healing', 'relationships', 'connection', 'communication'],
    description: 'Solfeggio frequency for relationships and connection',
    detailedInfo: 'Solfeggio frequency for relationships, connection, and communication. Helps improve interpersonal relationships.',
    effects: ['Relationships', 'Connection', 'Communication', 'Harmony'],
    recommendedDuration: 30
  },
  {
    id: 'solfeggio-741',
    name: 'Solfeggio 741Hz',
    frequency: 741,
    category: 'experimental',
    tags: ['solfeggio', 'healing', 'expression', 'solutions', 'awakening'],
    description: 'Solfeggio frequency for expression, solutions, and awakening',
    detailedInfo: 'Solfeggio frequency for expression, finding solutions, and awakening intuition. Helps solve problems and express creativity.',
    effects: ['Expression', 'Solutions', 'Awakening intuition', 'Problem-solving'],
    recommendedDuration: 30
  },
  {
    id: 'solfeggio-852',
    name: 'Solfeggio 852Hz',
    frequency: 852,
    category: 'experimental',
    tags: ['solfeggio', 'healing', 'intuition', 'spiritual-order', 'returning'],
    description: 'Solfeggio frequency for intuition and spiritual order',
    detailedInfo: 'Solfeggio frequency for returning to spiritual order, awakening intuition, and connecting with spiritual self.',
    effects: ['Intuition', 'Spiritual order', 'Awakening', 'Spiritual connection'],
    recommendedDuration: 30
  },
  {
    id: 'solfeggio-963',
    name: 'Solfeggio 963Hz',
    frequency: 963,
    category: 'experimental',
    tags: ['solfeggio', 'healing', 'oneness', 'unity', 'pineal-gland'],
    description: 'Solfeggio frequency for oneness and unity consciousness',
    detailedInfo: 'Solfeggio frequency for oneness, unity consciousness, and pineal gland activation. Connects with the higher self.',
    effects: ['Oneness', 'Unity consciousness', 'Pineal activation', 'Higher self connection'],
    recommendedDuration: 30
  },

  // Therapeutic Frequencies
  {
    id: 'therapeutic-432',
    name: 'A432 (Natural Tuning)',
    frequency: 432,
    category: 'experimental',
    tags: ['therapeutic', 'natural-tuning', 'harmony', 'healing'],
    description: 'A432 natural tuning frequency for harmony and healing',
    detailedInfo: 'A432 Hz is known as "natural tuning" or "Verdi\'s A". Some believe it resonates with nature and promotes harmony and healing.',
    effects: ['Natural harmony', 'Healing', 'Resonance with nature', 'Balance'],
    recommendedDuration: 30
  },
  {
    id: 'therapeutic-256',
    name: 'C256 (Middle C)',
    frequency: 256,
    category: 'experimental',
    tags: ['therapeutic', 'foundation', 'balance', 'healing'],
    description: 'C256 foundation frequency for balance and healing',
    detailedInfo: 'C256 Hz is the foundation frequency in some tuning systems. Associated with balance, foundation, and healing.',
    effects: ['Foundation', 'Balance', 'Healing', 'Stability'],
    recommendedDuration: 30
  },
  {
    id: 'therapeutic-136.1',
    name: 'OM Frequency 136.1Hz',
    frequency: 136.1,
    category: 'experimental',
    tags: ['therapeutic', 'om', 'meditation', 'spiritual'],
    description: 'OM frequency for meditation and spiritual connection',
    detailedInfo: '136.1 Hz is associated with the OM sound and is used in meditation and spiritual practices for connection and harmony.',
    effects: ['Meditation', 'Spiritual connection', 'Harmony', 'OM resonance'],
    recommendedDuration: 30
  },

  // Binaural Beats - Proven and Popular
  {
    id: 'binaural-1.5',
    name: 'Deep Sleep Binaural Beat 1.5Hz',
    frequency: 1.5,
    category: 'delta',
    tags: ['binaural-beats', 'sleep', 'deep-sleep', 'healing'],
    description: 'Proven binaural beat for deep sleep and healing',
    detailedInfo: '1.5 Hz delta binaural beat is one of the most popular frequencies for deep sleep. Research shows delta waves (0.5-4 Hz) are associated with deep sleep, healing, and restoration.',
    effects: ['Deep sleep', 'Physical healing', 'Immune system support', 'Growth hormone release'],
    recommendedDuration: 60
  },
  {
    id: 'binaural-4.5',
    name: 'Meditation Binaural Beat 4.5Hz',
    frequency: 4.5,
    category: 'theta',
    tags: ['binaural-beats', 'meditation', 'deep-relaxation', 'creativity'],
    description: 'Popular theta binaural beat for deep meditation',
    detailedInfo: '4.5 Hz theta binaural beat is widely used for meditation and deep relaxation. Theta waves (4-8 Hz) are associated with deep meditation, REM sleep, and enhanced creativity.',
    effects: ['Deep meditation', 'Enhanced creativity', 'Access to subconscious', 'Meditation enhancement'],
    recommendedDuration: 30
  },
  {
    id: 'binaural-6',
    name: 'Creativity Binaural Beat 6Hz',
    frequency: 6,
    category: 'theta',
    tags: ['binaural-beats', 'creativity', 'meditation', 'intuition'],
    description: 'Proven binaural beat for enhanced creativity',
    detailedInfo: '6 Hz theta binaural beat is popular for accessing creative states and intuitive insights. Research shows theta waves enhance creativity and problem-solving abilities.',
    effects: ['Enhanced creativity', 'Deep meditation', 'Subconscious access', 'Intuitive insights'],
    recommendedDuration: 30
  },
  {
    id: 'binaural-10',
    name: 'Relaxation Binaural Beat 10Hz',
    frequency: 10,
    category: 'alpha',
    tags: ['binaural-beats', 'relaxation', 'calm', 'stress-relief'],
    description: 'Most popular alpha binaural beat for relaxation',
    detailedInfo: '10 Hz alpha binaural beat is the most popular frequency for relaxation and stress relief. It corresponds to the Schumann resonance - Earth\'s natural frequency. Alpha waves (8-14 Hz) promote relaxed alertness and stress reduction.',
    effects: ['Stress reduction', 'Relaxed awareness', 'Improved learning', 'Calm focus'],
    recommendedDuration: 20
  },
  {
    id: 'binaural-15',
    name: 'Focus Binaural Beat 15Hz',
    frequency: 15,
    category: 'beta',
    tags: ['binaural-beats', 'focus', 'concentration', 'productivity'],
    description: 'Proven beta binaural beat for focus and concentration',
    detailedInfo: '15 Hz beta binaural beat is popular for enhancing focus and concentration. Beta waves (14-30 Hz) are associated with active thinking, problem-solving, and sustained attention.',
    effects: ['Improved focus', 'Enhanced concentration', 'Active thinking', 'Mental clarity'],
    recommendedDuration: 25
  },
  {
    id: 'binaural-20',
    name: 'Alertness Binaural Beat 20Hz',
    frequency: 20,
    category: 'beta',
    tags: ['binaural-beats', 'alertness', 'energy', 'productivity'],
    description: 'Popular beta binaural beat for alertness and energy',
    detailedInfo: '20 Hz beta binaural beat is used for increased alertness and mental energy. Higher beta frequencies (18-30 Hz) promote active engagement and sustained attention.',
    effects: ['Increased alertness', 'Mental energy', 'Active engagement', 'Sustained attention'],
    recommendedDuration: 20
  },
  {
    id: 'binaural-40',
    name: 'Peak Performance Binaural Beat 40Hz',
    frequency: 40,
    category: 'gamma',
    tags: ['binaural-beats', 'peak-performance', 'insight', 'memory'],
    description: 'Proven gamma binaural beat for peak performance',
    detailedInfo: '40 Hz gamma binaural beat is associated with peak performance, insight, and cognitive binding. Gamma waves (30-100 Hz) are linked to heightened perception, learning, and memory recall.',
    effects: ['Peak performance', 'Enhanced insight', 'Cognitive binding', 'Heightened awareness'],
    recommendedDuration: 15
  },
  {
    id: 'binaural-8',
    name: 'Meditation Bridge Binaural Beat 8Hz',
    frequency: 8,
    category: 'alpha',
    tags: ['binaural-beats', 'meditation', 'alpha-theta-bridge', 'intuition'],
    description: 'Popular alpha-theta bridge binaural beat',
    detailedInfo: '8 Hz binaural beat bridges alpha and theta states, making it popular for deep meditation while maintaining awareness. This frequency is often used for accessing altered states of consciousness.',
    effects: ['Deep meditation', 'Altered consciousness', 'Enhanced intuition', 'Inner peace'],
    recommendedDuration: 30
  },
  {
    id: 'binaural-3',
    name: 'Deep Healing Binaural Beat 3Hz',
    frequency: 3,
    category: 'delta',
    tags: ['binaural-beats', 'healing', 'deep-sleep', 'restoration'],
    description: 'Proven delta binaural beat for deep healing',
    detailedInfo: '3 Hz delta binaural beat is popular for deep healing and restorative sleep. Delta waves (0.5-4 Hz) are associated with deep sleep, physical healing, and immune system support.',
    effects: ['Deep sleep', 'Healing', 'Restoration', 'Recovery'],
    recommendedDuration: 60
  },
  {
    id: 'binaural-12',
    name: 'Calm Focus Binaural Beat 12Hz',
    frequency: 12,
    category: 'alpha',
    tags: ['binaural-beats', 'relaxation', 'calm', 'balance'],
    description: 'Popular upper alpha binaural beat for calm focus',
    detailedInfo: '12 Hz alpha binaural beat promotes relaxed focus and calm alertness without drowsiness. Upper alpha frequencies (10-14 Hz) are ideal for stress relief and mental clarity.',
    effects: ['Relaxed focus', 'Calm alertness', 'Stress relief', 'Mental clarity'],
    recommendedDuration: 25
  }
];

export const getFrequencyById = (id: string): Frequency | undefined => {
  return frequencies.find(f => f.id === id);
};

export const getFrequenciesByCategory = (category: Frequency['category']): Frequency[] => {
  return frequencies.filter(f => f.category === category);
};

export const getFrequenciesByTag = (tag: string): Frequency[] => {
  return frequencies.filter(f => f.tags.includes(tag));
};

export const searchFrequencies = (query: string): Frequency[] => {
  const lowerQuery = query.toLowerCase();
  return frequencies.filter(f => 
    f.name.toLowerCase().includes(lowerQuery) ||
    f.description.toLowerCase().includes(lowerQuery) ||
    f.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
  );
};
